import { useState, useMemo, useCallback } from "react";
import { useSharedStatus } from "../components/SharedStatusBadge";
import { NetworkConfiguration } from "../types";
import { CompanyConfig } from "Models/company.config";
import { useTranslation } from "react-i18next";

export const useNetworkSettings = () => {
  const [orderExpanded, setOrderExpanded] = useState(true);
  const [detailExpanded, setDetailExpanded] = useState(true);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [materialExpanded, setMaterialExpanded] = useState(false);
  const [taskExpanded, setTaskExpanded] = useState(false);
  const [accessExpanded, setAccessExpanded] = useState(false);
  const [instructionExpanded, setInstructionExpanded] = useState(false);
  const [folderExpanded, setFolderExpanded] = useState(false);

  const [detailsSelected, setDetailsSelected] = useState<string[]>(["reference"]);
  const [rolesSelected, setRolesSelected] = useState<string[]>([]);
  const [weightMeasurementsSelected, setWeightMeasurementsSelected] = useState<string[]>([]);
  const [pickupSelected, setPickupSelected] = useState<string[]>([]);
  const [deliverySelected, setDeliverySelected] = useState<string[]>([]);
  const [generalCommentsSelected, setGeneralCommentsSelected] = useState<string[]>([]);
  const [actualInventorySelected, setActualInventorySelected] = useState<string[]>([]);
  const [quotedInventorySelected, setQuotedInventorySelected] = useState<string[]>([]);
  const [materialSelected, setMaterialSelected] = useState<string[]>([]);
  const [taskSelected, setTaskSelected] = useState<string[]>([]);
  const [accessSelected, setAccessSelected] = useState<string[]>([]);
  const [instructionSelected, setInstructionSelected] = useState<string[]>([]);
  const [photosVideosSelected, setPhotosVideosSelected] = useState<string[]>([]);
  const [documentsSelected, setDocumentsSelected] = useState<string[]>([]);

  const { t: translate } = useTranslation();

  const FIELD_OPTIONS = useMemo(
    () => ({
      details: [
        { value: "reference", label: translate("reference") },
        { value: "2ndReference", label: translate("secondReference") },
        { value: "orderType", label: translate("orderType") },
        { value: "tag", label: translate("tag") },
        { value: "mode", label: translate("mode") },
        { value: "transportationType", label: translate("transportationType") },
        { value: "vipTreatment", label: translate("vipTreatment") },
        { value: "accountType", label: translate("accountType") },
        { value: "leadSource", label: translate("leadSource") },
        { value: "scac", label: translate("scac") },
      ],
      roles: [
        { value: "bookingAgent", label: translate("bookingAgent") },
        { value: "branchOrigin", label: translate("branchOrigin") },
        { value: "branchDestination", label: translate("branchDestination") },
        { value: "consultant", label: translate("consultant") },
        { value: "moveCoordinator", label: translate("moveCoordinator") },
        { value: "manager", label: translate("manager") },
      ],
      weightMeasurements: [
        { value: "allowance", label: translate("allowance") },
        { value: "inventorySummary", label: translate("inventorySummary") },
      ],
      pickup: [
        { value: "contact", label: translate("contact") },
        { value: "address", label: translate("address") },
      ],
      delivery: [
        { value: "contact", label: translate("contact") },
        { value: "address", label: translate("address") },
      ],
      generalComments: [{ value: "view", label: translate("view") }],
      actualInventory: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      quotedInventory: [{ value: "view", label: translate("view") }],
      material: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      task: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      access: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      instruction: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      photosVideos: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
      documents: [
        { value: "view", label: translate("view") },
        { value: "edit", label: translate("edit") },
        { value: "addNew", label: translate("addNew") },
      ],
    }),
    [translate]
  );

  const createFieldHandler = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
      (value: string, checked: boolean) => {
        setter((prev) => (checked ? [...prev, value] : prev.filter((item) => item !== value)));
      },
    []
  );

  const handlers = useMemo(
    () => ({
      handleDetailsChange: createFieldHandler(setDetailsSelected),
      handleRolesChange: createFieldHandler(setRolesSelected),
      handleWeightMeasurementsChange: createFieldHandler(setWeightMeasurementsSelected),
      handlePickupChange: createFieldHandler(setPickupSelected),
      handleDeliveryChange: createFieldHandler(setDeliverySelected),
      handleGeneralCommentsChange: createFieldHandler(setGeneralCommentsSelected),
      handleActualInventoryChange: createFieldHandler(setActualInventorySelected),
      handleQuotedInventoryChange: createFieldHandler(setQuotedInventorySelected),
      handleMaterialChange: createFieldHandler(setMaterialSelected),
      handleTaskChange: createFieldHandler(setTaskSelected),
      handleAccessChange: createFieldHandler(setAccessSelected),
      handleInstructionChange: createFieldHandler(setInstructionSelected),
      handlePhotosVideosChange: createFieldHandler(setPhotosVideosSelected),
      handleDocumentsChange: createFieldHandler(setDocumentsSelected),
    }),
    [createFieldHandler]
  );

  const allOrderOptions = useMemo(
    () => [
      ...FIELD_OPTIONS.details,
      ...FIELD_OPTIONS.roles,
      ...FIELD_OPTIONS.weightMeasurements,
      ...FIELD_OPTIONS.pickup,
      ...FIELD_OPTIONS.delivery,
      ...FIELD_OPTIONS.generalComments,
      ...FIELD_OPTIONS.actualInventory,
      ...FIELD_OPTIONS.quotedInventory,
      ...FIELD_OPTIONS.material,
      ...FIELD_OPTIONS.task,
      ...FIELD_OPTIONS.access,
      ...FIELD_OPTIONS.instruction,
      ...FIELD_OPTIONS.photosVideos,
      ...FIELD_OPTIONS.documents,
    ],
    []
  );

  const allOrderSelected = useMemo(
    () => [
      ...detailsSelected,
      ...rolesSelected,
      ...weightMeasurementsSelected,
      ...pickupSelected,
      ...deliverySelected,
      ...generalCommentsSelected,
      ...actualInventorySelected,
      ...quotedInventorySelected,
      ...materialSelected,
      ...taskSelected,
      ...accessSelected,
      ...instructionSelected,
      ...photosVideosSelected,
      ...documentsSelected,
    ],
    [
      detailsSelected,
      rolesSelected,
      weightMeasurementsSelected,
      pickupSelected,
      deliverySelected,
      generalCommentsSelected,
      actualInventorySelected,
      quotedInventorySelected,
      materialSelected,
      taskSelected,
      accessSelected,
      instructionSelected,
      photosVideosSelected,
      documentsSelected,
    ]
  );

  const orderStatus = useSharedStatus(
    allOrderSelected,
    allOrderOptions.map((opt) => opt.value)
  );

  const loadConfigFromAPI = useCallback((companyConfig: CompanyConfig) => {
    const networkConfig = companyConfig?.settings?.networkConfiguration;

    if (!networkConfig) {
      console.warn("No network configuration found in company config");
      return;
    }

    const detailSection = networkConfig.order?.sections?.detail;
    if (detailSection?.fields) {
      const detailsArray = Object.entries(detailSection.fields.details || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setDetailsSelected(detailsArray);

      const rolesArray = Object.entries(detailSection.fields.roles || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setRolesSelected(rolesArray);

      const weightMeasurementsArray = Object.entries(detailSection.fields.weightMeasurements || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setWeightMeasurementsSelected(weightMeasurementsArray);

      const pickupArray = Object.entries(detailSection.fields.pickup || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setPickupSelected(pickupArray);

      const deliveryArray = Object.entries(detailSection.fields.delivery || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setDeliverySelected(deliveryArray);

      const generalCommentsArray = Object.entries(detailSection.fields.generalComments || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setGeneralCommentsSelected(generalCommentsArray);
    }

    const inventorySection = networkConfig.order?.sections?.inventory;
    if (inventorySection?.fields) {
      const actualInventoryArray = Object.entries(inventorySection.fields.actualInventory || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setActualInventorySelected(actualInventoryArray);

      const quotedInventoryArray = Object.entries(inventorySection.fields.quotedInventory || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setQuotedInventorySelected(quotedInventoryArray);
    }

    const materialSection = networkConfig.order?.sections?.material;
    if (materialSection?.fields) {
      const materialArray = Object.entries(materialSection.fields.general || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setMaterialSelected(materialArray);
    }

    const taskSection = networkConfig.order?.sections?.task;
    if (taskSection?.fields) {
      const taskArray = Object.entries(taskSection.fields.general || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setTaskSelected(taskArray);
    }

    const accessSection = networkConfig.order?.sections?.access;
    if (accessSection?.fields) {
      const accessArray = Object.entries(accessSection.fields.general || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setAccessSelected(accessArray);
    }

    const instructionSection = networkConfig.order?.sections?.instruction;
    if (instructionSection?.fields) {
      const instructionArray = Object.entries(instructionSection.fields.general || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setInstructionSelected(instructionArray);
    }

    const folderSection = networkConfig.order?.sections?.folder;
    if (folderSection?.fields) {
      const photosVideosArray = Object.entries(folderSection.fields.photosVideos || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setPhotosVideosSelected(photosVideosArray);

      const documentsArray = Object.entries(folderSection.fields.documents || {})
        .filter(([, value]) => value)
        .map(([key]) => key);
      setDocumentsSelected(documentsArray);
    }

    setDetailExpanded(detailSection?.enabled || false);
    setInventoryExpanded(inventorySection?.enabled || false);
    setMaterialExpanded(materialSection?.enabled || false);
    setTaskExpanded(taskSection?.enabled || false);
    setAccessExpanded(accessSection?.enabled || false);
    setInstructionExpanded(instructionSection?.enabled || false);
    setFolderExpanded(folderSection?.enabled || false);
    setOrderExpanded(networkConfig.order?.enabled || true);
  }, []);

  const arrayToBooleanObject = (
    selectedArray: string[],
    allOptions: { value: string; label: string }[]
  ) => {
    const result: { [key: string]: boolean } = {};
    allOptions.forEach((option) => {
      result[option.value] = selectedArray.includes(option.value);
    });
    return result;
  };

  const getNetworkConfig = useCallback((): NetworkConfiguration => {
    const config: NetworkConfiguration = {
      order: {
        enabled: allOrderSelected.length > 0,
        sections: {
          detail: {
            enabled:
              [
                ...detailsSelected,
                ...rolesSelected,
                ...weightMeasurementsSelected,
                ...pickupSelected,
                ...deliverySelected,
                ...generalCommentsSelected,
              ].length > 0,
            fields: {
              details: arrayToBooleanObject(detailsSelected, FIELD_OPTIONS.details) as any,
              roles: arrayToBooleanObject(rolesSelected, FIELD_OPTIONS.roles) as any,
              weightMeasurements: arrayToBooleanObject(
                weightMeasurementsSelected,
                FIELD_OPTIONS.weightMeasurements
              ) as any,
              pickup: arrayToBooleanObject(pickupSelected, FIELD_OPTIONS.pickup) as any,
              delivery: arrayToBooleanObject(deliverySelected, FIELD_OPTIONS.delivery) as any,
              generalComments: arrayToBooleanObject(
                generalCommentsSelected,
                FIELD_OPTIONS.generalComments
              ) as any,
            },
          },
          inventory: {
            enabled: [...actualInventorySelected, ...quotedInventorySelected].length > 0,
            fields: {
              actualInventory: arrayToBooleanObject(
                actualInventorySelected,
                FIELD_OPTIONS.actualInventory
              ) as any,
              quotedInventory: arrayToBooleanObject(
                quotedInventorySelected,
                FIELD_OPTIONS.quotedInventory
              ) as any,
            },
          },
          material: {
            enabled: materialSelected.length > 0,
            fields: {
              general: arrayToBooleanObject(materialSelected, FIELD_OPTIONS.material) as any,
            },
          },
          task: {
            enabled: taskSelected.length > 0,
            fields: {
              general: arrayToBooleanObject(taskSelected, FIELD_OPTIONS.task) as any,
            },
          },
          access: {
            enabled: accessSelected.length > 0,
            fields: {
              general: arrayToBooleanObject(accessSelected, FIELD_OPTIONS.access) as any,
            },
          },
          instruction: {
            enabled: instructionSelected.length > 0,
            fields: {
              general: arrayToBooleanObject(instructionSelected, FIELD_OPTIONS.instruction) as any,
            },
          },
          folder: {
            enabled: [...photosVideosSelected, ...documentsSelected].length > 0,
            fields: {
              photosVideos: arrayToBooleanObject(
                photosVideosSelected,
                FIELD_OPTIONS.photosVideos
              ) as any,
              documents: arrayToBooleanObject(documentsSelected, FIELD_OPTIONS.documents) as any,
            },
          },
        },
      },
    };

    return config;
  }, [
    allOrderSelected.length,
    detailsSelected,
    rolesSelected,
    weightMeasurementsSelected,
    pickupSelected,
    deliverySelected,
    generalCommentsSelected,
    actualInventorySelected,
    quotedInventorySelected,
    materialSelected,
    taskSelected,
    accessSelected,
    instructionSelected,
    photosVideosSelected,
    documentsSelected,
  ]);

  const resetToInitialState = useCallback(() => {
    setDetailsSelected([]);
    setRolesSelected([]);
    setWeightMeasurementsSelected([]);
    setPickupSelected([]);
    setDeliverySelected([]);
    setGeneralCommentsSelected([]);
    setActualInventorySelected([]);
    setQuotedInventorySelected([]);
    setMaterialSelected([]);
    setTaskSelected([]);
    setAccessSelected([]);
    setInstructionSelected([]);
    setPhotosVideosSelected([]);
    setDocumentsSelected([]);

    setOrderExpanded(false);
    setDetailExpanded(false);
    setInventoryExpanded(false);
    setMaterialExpanded(false);
    setTaskExpanded(false);
    setAccessExpanded(false);
    setInstructionExpanded(false);
    setFolderExpanded(false);
  }, []);

  const selectAllHandlers = useMemo(
    () => ({
      handleDetailSelectAll: () => {
        const allDetailOptions = [
          ...FIELD_OPTIONS.details,
          ...FIELD_OPTIONS.roles,
          ...FIELD_OPTIONS.weightMeasurements,
          ...FIELD_OPTIONS.pickup,
          ...FIELD_OPTIONS.delivery,
          ...FIELD_OPTIONS.generalComments,
        ];
        const allDetailSelected = [
          ...detailsSelected,
          ...rolesSelected,
          ...weightMeasurementsSelected,
          ...pickupSelected,
          ...deliverySelected,
          ...generalCommentsSelected,
        ];

        const isAllSelected = allDetailSelected.length === allDetailOptions.length;

        if (isAllSelected) {
          setDetailsSelected([]);
          setRolesSelected([]);
          setWeightMeasurementsSelected([]);
          setPickupSelected([]);
          setDeliverySelected([]);
          setGeneralCommentsSelected([]);
        } else {
          setDetailsSelected(FIELD_OPTIONS.details.map((opt) => opt.value));
          setRolesSelected(FIELD_OPTIONS.roles.map((opt) => opt.value));
          setWeightMeasurementsSelected(FIELD_OPTIONS.weightMeasurements.map((opt) => opt.value));
          setPickupSelected(FIELD_OPTIONS.pickup.map((opt) => opt.value));
          setDeliverySelected(FIELD_OPTIONS.delivery.map((opt) => opt.value));
          setGeneralCommentsSelected(FIELD_OPTIONS.generalComments.map((opt) => opt.value));
        }
      },

      handleInventorySelectAll: () => {
        const allInventoryOptions = [
          ...FIELD_OPTIONS.actualInventory,
          ...FIELD_OPTIONS.quotedInventory,
        ];
        const allInventorySelected = [...actualInventorySelected, ...quotedInventorySelected];
        const isAllSelected = allInventorySelected.length === allInventoryOptions.length;

        if (isAllSelected) {
          setActualInventorySelected([]);
          setQuotedInventorySelected([]);
        } else {
          setActualInventorySelected(FIELD_OPTIONS.actualInventory.map((opt) => opt.value));
          setQuotedInventorySelected(FIELD_OPTIONS.quotedInventory.map((opt) => opt.value));
        }
      },

      handleMaterialSelectAll: () => {
        const isAllSelected = materialSelected.length === FIELD_OPTIONS.material.length;
        setMaterialSelected(isAllSelected ? [] : FIELD_OPTIONS.material.map((opt) => opt.value));
      },

      handleTaskSelectAll: () => {
        const isAllSelected = taskSelected.length === FIELD_OPTIONS.task.length;
        setTaskSelected(isAllSelected ? [] : FIELD_OPTIONS.task.map((opt) => opt.value));
      },

      handleAccessSelectAll: () => {
        const isAllSelected = accessSelected.length === FIELD_OPTIONS.access.length;
        setAccessSelected(isAllSelected ? [] : FIELD_OPTIONS.access.map((opt) => opt.value));
      },

      handleInstructionSelectAll: () => {
        const isAllSelected = instructionSelected.length === FIELD_OPTIONS.instruction.length;
        setInstructionSelected(
          isAllSelected ? [] : FIELD_OPTIONS.instruction.map((opt) => opt.value)
        );
      },

      handleFolderSelectAll: () => {
        const allFolderOptions = [...FIELD_OPTIONS.photosVideos, ...FIELD_OPTIONS.documents];
        const allFolderSelected = [...photosVideosSelected, ...documentsSelected];
        const isAllSelected = allFolderSelected.length === allFolderOptions.length;

        if (isAllSelected) {
          setPhotosVideosSelected([]);
          setDocumentsSelected([]);
        } else {
          setPhotosVideosSelected(FIELD_OPTIONS.photosVideos.map((opt) => opt.value));
          setDocumentsSelected(FIELD_OPTIONS.documents.map((opt) => opt.value));
        }
      },
    }),
    [
      detailsSelected,
      rolesSelected,
      weightMeasurementsSelected,
      pickupSelected,
      deliverySelected,
      generalCommentsSelected,
      actualInventorySelected,
      quotedInventorySelected,
      materialSelected,
      taskSelected,
      accessSelected,
      instructionSelected,
      photosVideosSelected,
      documentsSelected,
    ]
  );

  return {
    accordionStates: {
      orderExpanded,
      setOrderExpanded,
      detailExpanded,
      setDetailExpanded,
      inventoryExpanded,
      setInventoryExpanded,
      materialExpanded,
      setMaterialExpanded,
      taskExpanded,
      setTaskExpanded,
      accessExpanded,
      setAccessExpanded,
      instructionExpanded,
      setInstructionExpanded,
      folderExpanded,
      setFolderExpanded,
    },

    fieldOptions: FIELD_OPTIONS,

    fieldStates: {
      detailsSelected,
      rolesSelected,
      weightMeasurementsSelected,
      pickupSelected,
      deliverySelected,
      generalCommentsSelected,
      actualInventorySelected,
      quotedInventorySelected,
      materialSelected,
      taskSelected,
      accessSelected,
      instructionSelected,
      photosVideosSelected,
      documentsSelected,
    },

    resetToInitialState,
    loadConfigFromAPI,
    fieldHandlers: handlers,
    selectAllHandlers,

    orderStatus,
    getNetworkConfig,
  };
};
